/* ============================================================
   HomePlunge Scroll-Reveal Engine
   Drop this into your Shopify theme as assets/reveal.js
   and include with: {{ 'reveal.js' | asset_url | script_tag }}

   Usage in any Liquid section:
     <div class="hp-reveal" data-anim="rise" style="--hp-delay: 100ms;">
       ...
     </div>

   Attributes:
     data-anim   fade | rise | rise-sm | scale | slide-left | slide-right
     --hp-delay  CSS variable for stagger (e.g. style="--hp-delay: 200ms")

   Performance notes:
   - Single shared IntersectionObserver for the whole page.
   - Elements animate once then are un-observed (no perf tax on scroll).
   - Respects prefers-reduced-motion.
   ============================================================ */

(function () {
  'use strict';

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function revealAll() {
    document.querySelectorAll('.hp-reveal').forEach(el => el.classList.add('is-visible'));
  }

  if (prefersReducedMotion) {
    document.addEventListener('DOMContentLoaded', revealAll);
    return;
  }

  const io = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        // Small RAF to batch class additions
        requestAnimationFrame(() => entry.target.classList.add('is-visible'));
        io.unobserve(entry.target);
      }
    });
  }, {
    root: null,
    rootMargin: '0px 0px -10% 0px',
    threshold: 0.12
  });

  function observe() {
    document.querySelectorAll('.hp-reveal:not(.is-visible)').forEach(el => io.observe(el));
  }

  // Observe on DOM ready + again after load (for late-inserted content)
  if (document.readyState !== 'loading') {
    observe();
  } else {
    document.addEventListener('DOMContentLoaded', observe);
  }
  window.addEventListener('load', observe);

  // Expose for dynamically-inserted content (e.g. AJAX, cart drawer)
  window.HPReveal = { observe, revealAll };
})();

/* ============================================================
   Marquee helper — infinite scroll loop for press logos etc.
   Auto-initializes any .hp-marquee container.
   Clones children once so the loop is seamless.
   ============================================================ */
(function () {
  'use strict';
  function initMarquees() {
    document.querySelectorAll('.hp-marquee[data-marquee]').forEach(m => {
      if (m.dataset.marqueeReady) return;
      m.dataset.marqueeReady = '1';
      const track = m.querySelector('.hp-marquee__track');
      if (!track) return;
      // Duplicate children for seamless loop
      const clone = track.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      m.appendChild(clone);
    });
  }
  if (document.readyState !== 'loading') initMarquees();
  else document.addEventListener('DOMContentLoaded', initMarquees);
})();

/* ============================================================
   Parallax — subtle, lightweight, opt-in via [data-parallax="0.1"]
   Value = multiplier of scroll offset (keep it small: 0.05–0.2)
   ============================================================ */
(function () {
  'use strict';
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  let items = [];
  let ticking = false;

  function collect() {
    items = Array.from(document.querySelectorAll('[data-parallax]'));
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      const vh = window.innerHeight;
      items.forEach(el => {
        const rect = el.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > vh) return;
        const speed = parseFloat(el.dataset.parallax) || 0.1;
        const centerOffset = (rect.top + rect.height / 2) - vh / 2;
        el.style.transform = `translate3d(0, ${(-centerOffset * speed).toFixed(2)}px, 0)`;
      });
      ticking = false;
    });
  }

  if (document.readyState !== 'loading') collect();
  else document.addEventListener('DOMContentLoaded', collect);
  window.addEventListener('load', collect);
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', collect);
})();
